<template>
  <div id="app" class="site">

    <header class="nav">
      <div class="container nav__inner">
        <router-link to="/" class="brand">hyunwook shin</router-link>
        <nav class="nav__links" aria-label="Sections">
          <router-link
            v-for="p in pages"
            :key="p.name"
            :to="p.path"
            :exact="p.path === '/'"
            class="nav__link">{{ p.nav }}</router-link>
        </nav>
        <div class="nav__social">
          <a href="https://github.com/hyunwookshin" class="icon-btn" aria-label="GitHub" title="GitHub">
            <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
          </a>
          <a href="https://www.linkedin.com/in/hwshin/" class="icon-btn" aria-label="LinkedIn" title="LinkedIn">
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
        </div>
      </div>
    </header>

    <section class="hero">
      <div v-if="page" class="container hero__inner">
        <p class="hero__eyebrow">{{ page.eyebrow }}</p>
        <h1 class="hero__title">{{ page.heading }}</h1>
        <p class="hero__intro">{{ page.intro }}</p>
        <a v-if="page.link" :href="page.link.href" class="hero__link">
          {{ page.link.label }} <span aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </section>

    <main class="container main">
      <router-view :key="$route.path"/>
    </main>

    <footer class="footer">
      <div class="container footer__inner">
        <p class="footer__copy">Hyunwook Shin &copy; {{ year }}</p>
        <nav class="footer__links" aria-label="Footer">
          <router-link v-for="p in pages" :key="p.name" :to="p.path">{{ p.nav }}</router-link>
        </nav>
      </div>
    </footer>

  </div>
</template>

<script>
import pages from './pages.json'

export default {
  name: 'app',
  data() {
    return {
      pages,
      year: new Date().getFullYear()
    }
  },
  computed: {
    page() {
      return this.$route.meta.page
    }
  }
}
</script>

<style>
:root {
  --ink: #0b1020;
  --text: #0f172a;
  --text-muted: #475569;
  --text-subtle: #64748b;
  --line: #e2e8f0;
  --bg: #f6f7fb;
  --surface: #ffffff;
  --accent: #6d5dfc;
  --accent-soft: #eeecff;
  --accent-2: #22d3ee;
  --radius: 16px;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06);
  --shadow-lg: 0 12px 32px -8px rgba(15, 23, 42, 0.18), 0 4px 10px -4px rgba(15, 23, 42, 0.08);
  --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

* {
  box-sizing: border-box;
}

body {
  background-color: var(--bg) !important;
  font-family: var(--font) !important;
  color: var(--text) !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

a,
a:hover {
  text-decoration: none;
}

.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

.site {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── Header ─────────────────────────────────────────── */
.nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(11, 16, 32, 0.82);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  backdrop-filter: saturate(160%) blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.nav__inner {
  display: flex;
  align-items: center;
  gap: 24px;
  height: 64px;
}
.brand {
  font-family: 'Montserrat', "Calibri Light", Arial, sans-serif;
  font-size: 1.3rem;
  font-weight: 300;
  letter-spacing: 0.01em;
  color: #fff !important;
  white-space: nowrap;
}
.nav__links {
  display: flex;
  gap: 4px;
  margin-left: auto;
}
.nav__link {
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.68) !important;
  white-space: nowrap;
  transition: color 0.15s, background-color 0.15s;
}
.nav__link:hover {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.06);
}
.nav__link.router-link-active {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.12);
}
.nav__social {
  display: flex;
  gap: 4px;
  padding-left: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.68) !important;
  transition: color 0.15s, background-color 0.15s;
}
.icon-btn:hover {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.08);
}

/* ── Hero ───────────────────────────────────────────── */
.hero {
  position: relative;
  overflow: hidden;
  background-color: var(--ink);
  color: #fff;
  margin-top: -64px;
  padding: 136px 0 104px;
}
.hero::before,
.hero::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
}
.hero::before {
  background: url('https://hyunwook.s3.us-east-2.amazonaws.com/images/servertop.png') center / cover no-repeat;
  opacity: 0.16;
}
.hero::after {
  background:
    radial-gradient(640px 320px at 12% 0%, rgba(109, 93, 252, 0.5), transparent 70%),
    radial-gradient(520px 300px at 92% 100%, rgba(34, 211, 238, 0.22), transparent 70%);
}
.hero__inner {
  position: relative;
  z-index: 1;
}
.hero__eyebrow {
  margin: 0 0 12px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-2);
}
.hero__title {
  margin: 0 0 16px;
  font-size: 3rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #fff;
}
.hero__intro {
  max-width: 640px;
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.72);
}
.hero__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 24px;
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink) !important;
  background-color: #fff;
  transition: transform 0.15s, box-shadow 0.15s;
}
.hero__link:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(109, 93, 252, 0.45);
}

/* ── Main ───────────────────────────────────────────── */
.main {
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  margin-top: -48px;
  padding-bottom: 72px;
}

/* ── Footer ─────────────────────────────────────────── */
.footer {
  border-top: 1px solid var(--line);
  background-color: var(--surface);
}
.footer__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 28px;
  padding-bottom: 28px;
}
.footer__copy {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-subtle);
}
.footer__links {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}
.footer__links a {
  font-size: 0.85rem;
  color: var(--text-muted) !important;
}
.footer__links a:hover {
  color: var(--accent) !important;
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 760px) {
  .container {
    padding: 0 16px;
  }
  .nav__inner {
    flex-wrap: wrap;
    height: auto;
    gap: 0;
    padding-top: 12px;
  }
  .nav__social {
    margin-left: auto;
    padding-left: 0;
    border-left: none;
  }
  .nav__links {
    order: 3;
    width: calc(100% + 32px);
    margin: 8px -16px 0;
    padding: 0 12px 10px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .nav__links::-webkit-scrollbar {
    display: none;
  }
  .hero {
    margin-top: 0;
    padding: 40px 0 80px;
  }
  .hero__title {
    font-size: 2.1rem;
  }
  .hero__intro {
    font-size: 0.98rem;
  }
}
</style>
